import { Alert, Card, Center, Stack, Switch, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import NoData from "@/components/no-data";
import Table from "@/components/table";
import TablePagination from "@/components/table/TablePagination";
import { FiltroColaborador } from "./ModalFilterColaboradores";
import { getColaboradoresFromEmpresaMaster, getConfig, updateColaboradorAdminLicenciada } from "../../firestore";

export const QUERY_KEY = "colaboradores-admin-licenciada";

const columnHelper = createColumnHelper<TColaborador>();

const DEFAULT_SORTING: SortingState = [{ id: "nome", desc: false }];

/**
 * Apply client-side filters to colaboradores
 * @param colaboradores - List of colaboradores to filter
 * @param filter - Filter criteria
 * @returns Filtered colaboradores
 */
function aplicarFiltros(colaboradores: TColaborador[], filter: FiltroColaborador): TColaborador[] {
  if (!filter.search || !filter.campo) {
    return colaboradores;
  }

  const searchTerm = filter.search.toLowerCase();

  return colaboradores.filter((item) => {
    switch (filter.campo) {
      case "nome": {
        return item.nome?.toLowerCase().includes(searchTerm) ?? false;
      }
      case "email": {
        return item.email?.toLowerCase().includes(searchTerm) ?? false;
      }
      case "role": {
        return item.role?.toLowerCase().includes(searchTerm) ?? false;
      }
      default: {
        return true;
      }
    }
  });
}

interface ColaboradoresAdminTableProperties {
  filter?: FiltroColaborador;
}

const ColaboradoresAdminTable = ({ filter }: ColaboradoresAdminTableProperties) => {
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  // Buscar config para obter empresaMaster
  const {
    data: config,
    isLoading: isLoadingConfig,
    error: configError
  } = useQuery({
    queryKey: ["config-empresaMaster"],
    queryFn: getConfig
  });

  // Lida com empresaMaster podendo ser string (legado) ou DocumentReference (novo)
  const empresaMasterPath =
    typeof config?.empresaMaster === "string" ? config.empresaMaster : config?.empresaMaster?.path;

  // Buscar colaboradores da empresaMaster
  const {
    data: colaboradores,
    isLoading: isLoadingColaboradores,
    error: colaboradoresError
  } = useQuery({
    queryKey: [QUERY_KEY, empresaMasterPath],
    queryFn: () => {
      if (!empresaMasterPath) {
        throw new Error("Caminho da empresa master não encontrado");
      }
      return getColaboradoresFromEmpresaMaster(empresaMasterPath);
    },
    enabled: !!empresaMasterPath
  });

  // Mutation para atualizar adminLicenciada
  const { mutate: updateAdminLicenciada } = useMutation({
    mutationFn: ({ colaboradorId, adminLicenciada }: { colaboradorId: string; adminLicenciada: boolean }) => {
      if (!empresaMasterPath) {
        throw new Error("Empresa master não encontrada");
      }
      return updateColaboradorAdminLicenciada(empresaMasterPath, colaboradorId, adminLicenciada);
    },
    onSuccess: (_, variables) => {
      showNotification({
        message: `Acesso ${variables.adminLicenciada ? "concedido" : "revogado"} com sucesso!`,
        color: "green",
        autoClose: 2500
      });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error) => {
      showNotification({
        message: `Erro ao atualizar acesso: ${error.message}`,
        color: "red",
        autoClose: 2500
      });
    }
  });

  const columns: ColumnDef<TColaborador>[] = [
    columnHelper.accessor("nome", {
      header: "Nome",
      enableSorting: true
    }),
    columnHelper.accessor("email", {
      header: "E-mail",
      enableSorting: true
    }),
    columnHelper.accessor("role", {
      header: "Perfil",
      enableSorting: true
    }),
    columnHelper.display({
      id: "adminLicenciada",
      header: "Acesso Admin",
      cell: (properties) => {
        const colaborador = properties.row.original;
        const handleAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          updateAdminLicenciada({
            colaboradorId: colaborador.id,
            adminLicenciada: event.currentTarget.checked
          });
        };
        return <Switch checked={colaborador.adminLicenciada === true} onChange={handleAdminChange} />;
      }
    })
  ];

  // Apply client-side filters
  const filteredData = useMemo(() => {
    if (!colaboradores) return [];
    if (!filter) return colaboradores;
    return aplicarFiltros(colaboradores, filter);
  }, [colaboradores, filter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const isLoading = isLoadingConfig || isLoadingColaboradores;
  const error = configError || colaboradoresError;

  // Create tableHandlers for TablePagination
  const tableHandlers = useMemo(
    () => ({
      table,
      data: {
        entries: filteredData,
        firstDocument: undefined,
        lastDocument: undefined,
        total: filteredData.length
      },
      isLoading,
      error: error,
      filters: []
    }),
    [table, filteredData, isLoading, error]
  );

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title="Erro" color="red">
        {error.message}
      </Alert>
    );
  }

  if (!isLoading && !empresaMasterPath) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title="Configuração não encontrada" color="yellow">
        O campo empresaMaster não foi configurado.
      </Alert>
    );
  }

  const isEmpty = !isLoading && filteredData.length === 0;

  return (
    <>
      <Card.Section withBorder>
        <Table table={table} isLoading={isLoading} />
        {isEmpty && (
          <Center m="lg">
            <NoData m="md">
              <Stack gap="xs">
                <Text c="dimmed">
                  {filter?.search
                    ? "Nenhum colaborador encontrado com o filtro aplicado!"
                    : "Nenhum colaborador encontrado na empresa master."}
                </Text>
              </Stack>
            </NoData>
          </Center>
        )}
      </Card.Section>
      <Card.Section withBorder>
        <TablePagination tableHandlers={tableHandlers} />
      </Card.Section>
    </>
  );
};

export default ColaboradoresAdminTable;
